import React, { useState, useEffect, useCallback } from "react";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import ProviderDragDropUpload from "../components/ProviderDragDropUpload";
import axios from "../api/Axios";

// Step 1: Provider Details and Contact Information
const Step1 = ({
  step1Data,
  handleStep1Change,
  validateStep1,
  submitStep1Data,
  loadingStates,
  nextStep,
  validationErrors,
  setValidationErrors,
  // Document upload props
  uploadedDocuments,
  uploadingDocuments,
  uploadedFilenames,
  getDocumentCardState,
  handleDocumentClick,
  resetAllUploadingStates,
  documentErrors,
  validateDocuments,
  selectedDocument,
  handleDocumentUploaded,
  handleDocumentUploadFailed,
  handleClearSelection,
  handleUploadStart,
  providerType
}) => {

  const handleNext = async () => {
    console.log('Step1 handleNext called');
    
    // First validate Step 1 form data
    const errors = validateStep1();
    console.log('Step1 validation errors:', errors);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Then validate documents
    console.log('Validating documents...');
    const documentsValid = validateDocuments();
    console.log('Documents valid:', documentsValid);
    
    if (!documentsValid) {
      // Scroll to documents section to show errors
      document.getElementById("Required Documents")?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      return;
    }
    
    setValidationErrors({});
    console.log('All validations passed, submitting');
    
    // Submit step 1 data
    const success = await submitStep1Data();
    console.log('Step1 data submission success:', success);
    if (success) {
      console.log('Calling nextStep to go to step 2');
      nextStep();
    }
  };

  // Cleanup function to reset all states on component unmount
  useEffect(() => {
    return () => {
      resetAllUploadingStates();
    };
  }, [resetAllUploadingStates]);

  return (
    <div>
      {/* Organization Section - Only for Entity providers */}
      {providerType === 'entity' && (
        <>
          <h3>Organization Identification</h3>
          
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.registration_number ? 'is-invalid' : ''}`}
                  type="text"
                  id="registrationNumberInput"
                  value={step1Data.registration_number}
                  onChange={(e) => handleStep1Change('registration_number', e.target.value)}
                  placeholder="Business Registration Number"
                />
                <label htmlFor="registrationNumberInput">Business Registration Number *</label>
                {validationErrors.registration_number && (
                  <div className="invalid-feedback">{validationErrors.registration_number}</div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-outline mb-3">
                <PhoneInput
                  className={`${validationErrors.organization_phone ? 'phone-input-invalid' : ''}`}
                  value={step1Data.organization_phone}
                  onChange={(value) => handleStep1Change('organization_phone', value || '')}
                  defaultCountry="ZA"
                  placeholder="Enter organization phone number"
                  international
                  countryCallingCodeEditable={false}
                />
                <label className="form-label">Organization Phone *</label>
                {validationErrors.organization_phone && (
                  <div className="invalid-feedback">{validationErrors.organization_phone}</div>
                )}
              </div>
            </div>
          </div>

          <h3>Representative Identification</h3>
        </>
      )}

      {/* Individual Identification or Representative Identification (for entities) */}
      {providerType === 'individual' && <h3>Identification</h3>}

      {/* Citizenship and ID Document Type - From Student Wizard */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.citizenship ? 'is-invalid' : ''}`}
              id="citizenshipSelect"
              value={step1Data.citizenship}
              onChange={(e) => handleStep1Change('citizenship', e.target.value)}
            >
              <option value="">Select Citizenship</option>
              <option value="South Africa">South Africa</option>
              <option value="Afghanistan">Afghanistan</option>
              <option value="Albania">Albania</option>
              <option value="Algeria">Algeria</option>
              <option value="Andorra">Andorra</option>
              <option value="Angola">Angola</option>
              <option value="Antigua and Barbuda">Antigua and Barbuda</option>
              <option value="Argentina">Argentina</option>
              <option value="Armenia">Armenia</option>
              <option value="Australia">Australia</option>
              <option value="Austria">Austria</option>
              <option value="Azerbaijan">Azerbaijan</option>
              <option value="Bahamas">Bahamas</option>
              <option value="Bahrain">Bahrain</option>
              <option value="Bangladesh">Bangladesh</option>
              <option value="Barbados">Barbados</option>
              <option value="Belarus">Belarus</option>
              <option value="Belgium">Belgium</option>
              <option value="Belize">Belize</option>
              <option value="Benin">Benin</option>
              <option value="Bhutan">Bhutan</option>
              <option value="Bolivia">Bolivia</option>
              <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
              <option value="Botswana">Botswana</option>
              <option value="Brazil">Brazil</option>
              <option value="Brunei">Brunei</option>
              <option value="Bulgaria">Bulgaria</option>
              <option value="Burkina Faso">Burkina Faso</option>
              <option value="Burundi">Burundi</option>
              <option value="Cabo Verde">Cabo Verde</option>
              <option value="Cambodia">Cambodia</option>
              <option value="Cameroon">Cameroon</option>
              <option value="Canada">Canada</option>
              <option value="Central African Republic">Central African Republic</option>
              <option value="Chad">Chad</option>
              <option value="Chile">Chile</option>
              <option value="China">China</option>
              <option value="Colombia">Colombia</option>
              <option value="Comoros">Comoros</option>
              <option value="Congo, Democratic Republic of the">Congo, Democratic Republic of the</option>
              <option value="Congo, Republic of the">Congo, Republic of the</option>
              <option value="Costa Rica">Costa Rica</option>
              <option value="Cote d'Ivoire">Cote d'Ivoire</option>
              <option value="Croatia">Croatia</option>
              <option value="Cuba">Cuba</option>
              <option value="Cyprus">Cyprus</option>
              <option value="Czech Republic">Czech Republic</option>
              <option value="Denmark">Denmark</option>
              <option value="Djibouti">Djibouti</option>
              <option value="Dominica">Dominica</option>
              <option value="Dominican Republic">Dominican Republic</option>
              <option value="East Timor">East Timor</option>
              <option value="Ecuador">Ecuador</option>
              <option value="Egypt">Egypt</option>
              <option value="El Salvador">El Salvador</option>
              <option value="Equatorial Guinea">Equatorial Guinea</option>
              <option value="Eritrea">Eritrea</option>
              <option value="Estonia">Estonia</option>
              <option value="Eswatini">Eswatini</option>
              <option value="Ethiopia">Ethiopia</option>
              <option value="Fiji">Fiji</option>
              <option value="Finland">Finland</option>
              <option value="France">France</option>
              <option value="Gabon">Gabon</option>
              <option value="Gambia">Gambia</option>
              <option value="Georgia">Georgia</option>
              <option value="Germany">Germany</option>
              <option value="Ghana">Ghana</option>
              <option value="Greece">Greece</option>
              <option value="Grenada">Grenada</option>
              <option value="Guatemala">Guatemala</option>
              <option value="Guinea">Guinea</option>
              <option value="Guinea-Bissau">Guinea-Bissau</option>
              <option value="Guyana">Guyana</option>
              <option value="Haiti">Haiti</option>
              <option value="Honduras">Honduras</option>
              <option value="Hungary">Hungary</option>
              <option value="Iceland">Iceland</option>
              <option value="India">India</option>
              <option value="Indonesia">Indonesia</option>
              <option value="Iran">Iran</option>
              <option value="Iraq">Iraq</option>
              <option value="Ireland">Ireland</option>
              <option value="Israel">Israel</option>
              <option value="Italy">Italy</option>
              <option value="Jamaica">Jamaica</option>
              <option value="Japan">Japan</option>
              <option value="Jordan">Jordan</option>
              <option value="Kazakhstan">Kazakhstan</option>
              <option value="Kenya">Kenya</option>
              <option value="Kiribati">Kiribati</option>
              <option value="Korea, North">Korea, North</option>
              <option value="Korea, South">Korea, South</option>
              <option value="Kosovo">Kosovo</option>
              <option value="Kuwait">Kuwait</option>
              <option value="Kyrgyzstan">Kyrgyzstan</option>
              <option value="Laos">Laos</option>
              <option value="Latvia">Latvia</option>
              <option value="Lebanon">Lebanon</option>
              <option value="Lesotho">Lesotho</option>
              <option value="Liberia">Liberia</option>
              <option value="Libya">Libya</option>
              <option value="Liechtenstein">Liechtenstein</option>
              <option value="Lithuania">Lithuania</option>
              <option value="Luxembourg">Luxembourg</option>
              <option value="Madagascar">Madagascar</option>
              <option value="Malawi">Malawi</option>
              <option value="Malaysia">Malaysia</option>
              <option value="Maldives">Maldives</option>
              <option value="Mali">Mali</option>
              <option value="Malta">Malta</option>
              <option value="Marshall Islands">Marshall Islands</option>
              <option value="Mauritania">Mauritania</option>
              <option value="Mauritius">Mauritius</option>
              <option value="Mexico">Mexico</option>
              <option value="Micronesia">Micronesia</option>
              <option value="Moldova">Moldova</option>
              <option value="Monaco">Monaco</option>
              <option value="Mongolia">Mongolia</option>
              <option value="Montenegro">Montenegro</option>
              <option value="Morocco">Morocco</option>
              <option value="Mozambique">Mozambique</option>
              <option value="Myanmar">Myanmar</option>
              <option value="Namibia">Namibia</option>
              <option value="Nauru">Nauru</option>
              <option value="Nepal">Nepal</option>
              <option value="Netherlands">Netherlands</option>
              <option value="New Zealand">New Zealand</option>
              <option value="Nicaragua">Nicaragua</option>
              <option value="Niger">Niger</option>
              <option value="Nigeria">Nigeria</option>
              <option value="North Macedonia">North Macedonia</option>
              <option value="Norway">Norway</option>
              <option value="Oman">Oman</option>
              <option value="Pakistan">Pakistan</option>
              <option value="Palau">Palau</option>
              <option value="Palestine">Palestine</option>
              <option value="Panama">Panama</option>
              <option value="Papua New Guinea">Papua New Guinea</option>
              <option value="Paraguay">Paraguay</option>
              <option value="Peru">Peru</option>
              <option value="Philippines">Philippines</option>
              <option value="Poland">Poland</option>
              <option value="Portugal">Portugal</option>
              <option value="Qatar">Qatar</option>
              <option value="Romania">Romania</option>
              <option value="Russia">Russia</option>
              <option value="Rwanda">Rwanda</option>
              <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
              <option value="Saint Lucia">Saint Lucia</option>
              <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
              <option value="Samoa">Samoa</option>
              <option value="San Marino">San Marino</option>
              <option value="Sao Tome and Principe">Sao Tome and Principe</option>
              <option value="Saudi Arabia">Saudi Arabia</option>
              <option value="Senegal">Senegal</option>
              <option value="Serbia">Serbia</option>
              <option value="Seychelles">Seychelles</option>
              <option value="Sierra Leone">Sierra Leone</option>
              <option value="Singapore">Singapore</option>
              <option value="Slovakia">Slovakia</option>
              <option value="Slovenia">Slovenia</option>
              <option value="Solomon Islands">Solomon Islands</option>
              <option value="Somalia">Somalia</option>
              <option value="South Africa">South Africa</option>
              <option value="South Sudan">South Sudan</option>
              <option value="Spain">Spain</option>
              <option value="Sri Lanka">Sri Lanka</option>
              <option value="Sudan">Sudan</option>
              <option value="Suriname">Suriname</option>
              <option value="Sweden">Sweden</option>
              <option value="Switzerland">Switzerland</option>
              <option value="Syria">Syria</option>
              <option value="Taiwan">Taiwan</option>
              <option value="Tajikistan">Tajikistan</option>
              <option value="Tanzania">Tanzania</option>
              <option value="Thailand">Thailand</option>
              <option value="Togo">Togo</option>
              <option value="Tonga">Tonga</option>
              <option value="Trinidad and Tobago">Trinidad and Tobago</option>
              <option value="Tunisia">Tunisia</option>
              <option value="Turkey">Turkey</option>
              <option value="Turkmenistan">Turkmenistan</option>
              <option value="Tuvalu">Tuvalu</option>
              <option value="Uganda">Uganda</option>
              <option value="Ukraine">Ukraine</option>
              <option value="United Arab Emirates">United Arab Emirates</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="United States">United States</option>
              <option value="Uruguay">Uruguay</option>
              <option value="Uzbekistan">Uzbekistan</option>
              <option value="Vanuatu">Vanuatu</option>
              <option value="Vatican City">Vatican City</option>
              <option value="Venezuela">Venezuela</option>
              <option value="Vietnam">Vietnam</option>
              <option value="Yemen">Yemen</option>
              <option value="Zambia">Zambia</option>
              <option value="Zimbabwe">Zimbabwe</option>
            </select>
            <label htmlFor="citizenshipSelect">Citizenship *</label>
            {validationErrors.citizenship && (
              <div className="invalid-feedback">{validationErrors.citizenship}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.id_document_type ? 'is-invalid' : ''}`}
              id="idDocumentTypeSelect"
              value={step1Data.id_document_type}
              onChange={(e) => handleStep1Change('id_document_type', e.target.value)}
            >
              <option value="">Select ID Document Type</option>
              <option value="passport">Passport</option>
              <option value="national_id">National ID</option>
              <option value="drivers_license">Driver's License</option>
              <option value="refugee travel document">Refugee Travel Document</option>
            </select>
            <label htmlFor="idDocumentTypeSelect">ID Document Type *</label>
            {validationErrors.id_document_type && (
              <div className="invalid-feedback">{validationErrors.id_document_type}</div>
            )}
          </div>
        </div>
      </div>

      {/* ID Number - Full width */}
      <div className="form-floating mb-3">
        <input
          className={`form-control ${validationErrors.id_number ? 'is-invalid' : ''}`}
          type="text"
          id="idNumberInput"
          value={step1Data.id_number}
          onChange={(e) => handleStep1Change('id_number', e.target.value)}
          placeholder="ID Number"
        />
        <label htmlFor="idNumberInput">ID Number *</label>
        {validationErrors.id_number && (
          <div className="invalid-feedback">{validationErrors.id_number}</div>
        )}
      </div>

      {/* Phone Number - Full width */}
      <div className="form-outline mb-3">
        <PhoneInput
          className={`${validationErrors.phone_number ? 'phone-input-invalid' : ''}`}
          value={step1Data.phone_number}
          onChange={(value) => handleStep1Change('phone_number', value || '')}
          defaultCountry="ZA"
          placeholder="Enter phone number"
          international
          countryCallingCodeEditable={false}
        />
        <label className="form-label">
          {providerType === 'entity' ? 'Representative Phone Number *' : 'Phone Number *'}
        </label>
        {validationErrors.phone_number && (
          <div className="invalid-feedback">{validationErrors.phone_number}</div>
        )}
      </div>

      {/* Required Documents */}
      <h3>Required Document</h3>
      <div id="Required Documents" className="row g-3 mb-3">
        
        {/* Single document upload based on provider type */}
        {providerType === 'individual' ? (
          <ProviderDragDropUpload
            documentType="id_passport"
            title="Photo of Identity/Passport Document"
            onDocumentUploaded={handleDocumentUploaded}
            onDocumentUploadFailed={handleDocumentUploadFailed}
            uploadedFilenames={uploadedFilenames}
            isUploading={uploadingDocuments.has('id_passport')}
            onUploadStart={handleUploadStart}
            hasError={documentErrors.has('id_passport')}
          />
        ) : (
          <ProviderDragDropUpload
            documentType="company_registration"
            title="Company Registration Certificate"
            onDocumentUploaded={handleDocumentUploaded}
            onDocumentUploadFailed={handleDocumentUploadFailed}
            uploadedFilenames={uploadedFilenames}
            isUploading={uploadingDocuments.has('company_registration')}
            onUploadStart={handleUploadStart}
            hasError={documentErrors.has('company_registration')}
          />
        )}
      </div>

      <button
        className="btn btn-primary mt-4"
        onClick={handleNext}
        disabled={loadingStates.step1}
      >
        {loadingStates.step1 ? 'Submitting...' : 'Continue to Address'}
      </button>

      {/* Reuse the exact same styles from StudentFormWizard */}
      <style>
        {`
          h3 {
            font-size: 22px;
            color: #495057;
            font-weight: 600;
            margin: 2rem 0 1.5rem 0;
            padding: 0.75rem 0;
            border-bottom: 2px solid #e9ecef;
            position: relative;
          }
          
          h3::before {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 60px;
            height: 2px;
            background: linear-gradient(45deg, #0d6efd, #6610f2);
            border-radius: 1px;
          }
          
          h3:first-child {
            margin-top: 0;
          }
          
          .doc-card {
            transition: box-shadow 0.2s, transform 0.3s ease;
            animation: fadeInSlide 0.4s ease-in-out;
          }
          
          .doc-card:hover {
            box-shadow: 0 0 0.5rem #0d6efd33;
            background: #f8f9fa;
            transform: translateY(-2px);
          }
          
          .doc-card.clickable {
            transition: all 0.3s ease;
            user-select: none;
          }
          
          .doc-card.clickable:hover {
            box-shadow: 0 0 1rem #0d6efd44;
            background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);
            transform: translateY(-3px);
            border-color: #0d6efd;
          }
          
          .doc-card.clickable:active {
            transform: translateY(-1px);
            box-shadow: 0 0 0.5rem #0d6efd33;
          }
          
          .bg-success-subtle {
            background-color: rgba(25, 135, 84, 0.1) !important;
          }
          
          .border-success {
            border-color: #198754 !important;
          }
          
          .bg-danger-subtle {
            background-color: rgba(220, 53, 69, 0.1) !important;
          }
          
          .border-danger {
            border-color: #dc3545 !important;
          }
          
          .bg-info-subtle {
            background-color: rgba(13, 110, 253, 0.1) !important;
          }
          
          .border-info {
            border-color: #0d6efd !important;
          }
          
          .position-absolute {
            position: absolute !important;
          }
          
          .top-0 {
            top: 0 !important;
          }
          
          .end-0 {
            right: 0 !important;
          }
          
          .p-2 {
            padding: 0.5rem !important;
          }
          
          .form-control.is-invalid,
          .form-select.is-invalid {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
            background-color: rgba(220, 53, 69, 0.05);
          }
          
          .form-control.is-invalid:focus,
          .form-select.is-invalid:focus {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
            background-color: rgba(220, 53, 69, 0.05);
          }
          
          .invalid-feedback {
            display: block;
            width: 100%;
            margin-top: 0.5rem;
            font-size: 0.875rem;
            color: #fff;
            text-align: left;
            position: relative;
            z-index: 1;
            padding: 0.5rem 0.75rem;
            background: #dc3545;
            border-radius: 0.375rem;
            border: 1px solid #dc3545;
          }
          
          .form-outline .invalid-feedback,
          .form-floating .invalid-feedback {
            margin-top: 0.5rem;
            position: relative;
            clear: both;
          }
          
          .invalid-feedback::before {
            content: '';
            position: absolute;
            top: -5px;
            left: 1rem;
            width: 0;
            height: 0;
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-bottom: 5px solid #dc3545;
          }
          
          .PhoneInput {
            position: relative;
            display: flex;
            align-items: center;
            border: 1px solid #ced4da;
            border-radius: 4px;
            background: transparent;
            padding: 0;
            font-size: 16px;
            line-height: 1.5;
            width: 100%;
            min-height: 58px;
            height: 58px;
          }
          
          .PhoneInput:focus-within {
            border-color: #0d6efd;
            box-shadow: none;
          }
          
          .PhoneInput.phone-input-invalid {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          }
          
          .PhoneInput.phone-input-invalid:focus-within {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          }
          
          .PhoneInputCountrySelect {
            border: none;
            background: transparent;
            margin: 0;
            padding: 16px 12px;
            font-size: 16px;
            outline: none;
            min-width: 80px;
            height: 100%;
          }
          
          .PhoneInputCountryIcon {
            margin-right: 8px;
            width: 20px;
            height: 15px;
          }
          
          .PhoneInputInput {
            border: none;
            background: transparent;
            padding: 16px 12px 16px 8px;
            font-size: 16px;
            line-height: 1.5;
            width: 100%;
            outline: none;
            flex: 1;
            height: 100%;
          }
          
          .form-outline:has(.PhoneInput) .form-label {
            top: -8px;
            font-size: 12px;
            color: #6c757d;
          }
          
          .form-outline:has(.PhoneInput:focus-within) .form-label {
            color: #0d6efd;
            font-size: 12px;
            top: -8px;
          }

          @keyframes fadeInSlide {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

// Completion Success Component
const CompletionSuccess = () => {
  return (
    <div style={{ 
      textAlign: 'center', 
      padding: '3rem 1rem',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      <div style={{ marginBottom: '2rem' }}>
        <i className="bi bi-check-circle-fill" style={{ fontSize: '4rem', color: '#198754' }}></i>
      </div>
      <div style={{ 
        backgroundColor: '#d1e7dd',
        border: '1px solid #badbcc',
        borderRadius: '0.375rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 0.5rem 1rem rgba(25, 135, 84, 0.15)'
      }}>
        <h4 style={{ color: '#0f5132', marginBottom: '1rem', fontWeight: 'bold' }}>
          <i className="bi bi-check-circle" style={{ marginRight: '0.5rem' }}></i>
          Registration Completed Successfully!
        </h4>
        <p style={{ color: '#0f5132', fontSize: '1.1rem', margin: '0' }}>
          Your provider registration has been submitted. We will review your documents and contact you shortly.
        </p>
      </div>
      <div style={{ color: '#6c757d' }}>
        <p style={{ marginBottom: '0.5rem' }}>Thank you for joining our accommodation provider network.</p>
        <p style={{ margin: '0' }}>You can now start managing your properties once approved.</p>
      </div>
    </div>
  );
};

// Step 2: Address Information
const Step2 = ({
  step2Data,
  handleStep2Change,
  validateStep2,
  submitStep2Data,
  loadingStates,
  previousStep,
  nextStep,
  validationErrors,
  setValidationErrors,
  providerType
}) => {

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateStep2();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setValidationErrors({});
    const success = await submitStep2Data();
    if (success) {
      nextStep();
    }
  };

  const handlePrevious = () => {
    setValidationErrors({});
    previousStep();
  };

  return (
    <div>
      {/* Entity providers need organization address section */}
      {providerType === 'entity' && (
        <>
          <h3>Organization Address</h3>
          
          {/* Country - Full width - Locked to South Africa */}
          <div className="form-floating mb-3">
            <input
              className="form-control"
              type="text"
              id="orgCountryInput"
              value="South Africa"
              readOnly
              style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
            />
            <label htmlFor="orgCountryInput">Country</label>
          </div>

          {/* Address Line 1 and 2 - Side by side */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.org_address_line_1 ? 'is-invalid' : ''}`}
                  type="text"
                  id="orgAddressLine1Input"
                  value={step2Data.org_address_line_1}
                  onChange={(e) => handleStep2Change('org_address_line_1', e.target.value)}
                  placeholder="Address Line 1"
                />
                <label htmlFor="orgAddressLine1Input">Address Line 1 *</label>
                {validationErrors.org_address_line_1 && (
                  <div className="invalid-feedback">{validationErrors.org_address_line_1}</div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className="form-control"
                  type="text"
                  id="orgAddressLine2Input"
                  value={step2Data.org_address_line_2}
                  onChange={(e) => handleStep2Change('org_address_line_2', e.target.value)}
                  placeholder="Address Line 2"
                />
                <label htmlFor="orgAddressLine2Input">Address Line 2</label>
              </div>
            </div>
          </div>

          {/* Suburb and City - Side by side */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.org_suburb ? 'is-invalid' : ''}`}
                  type="text"
                  id="orgSuburbInput"
                  value={step2Data.org_suburb}
                  onChange={(e) => handleStep2Change('org_suburb', e.target.value)}
                  placeholder="Suburb"
                />
                <label htmlFor="orgSuburbInput">Suburb *</label>
                {validationErrors.org_suburb && (
                  <div className="invalid-feedback">{validationErrors.org_suburb}</div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.org_city ? 'is-invalid' : ''}`}
                  type="text"
                  id="orgCityInput"
                  value={step2Data.org_city}
                  onChange={(e) => handleStep2Change('org_city', e.target.value)}
                  placeholder="City"
                />
                <label htmlFor="orgCityInput">City *</label>
                {validationErrors.org_city && (
                  <div className="invalid-feedback">{validationErrors.org_city}</div>
                )}
              </div>
            </div>
          </div>

          {/* Postcode and Province - Side by side */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <input
                  className={`form-control ${validationErrors.org_postcode ? 'is-invalid' : ''}`}
                  type="text"
                  id="orgPostcodeInput"
                  value={step2Data.org_postcode}
                  onChange={(e) => handleStep2Change('org_postcode', e.target.value)}
                  placeholder="Postcode"
                />
                <label htmlFor="orgPostcodeInput">Postcode *</label>
                {validationErrors.org_postcode && (
                  <div className="invalid-feedback">{validationErrors.org_postcode}</div>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <select
                  className={`form-select ${validationErrors.org_province ? 'is-invalid' : ''}`}
                  id="orgProvinceSelect"
                  value={step2Data.org_province}
                  onChange={(e) => handleStep2Change('org_province', e.target.value)}
                >
                  <option value="">Select Province</option>
                  <option value="Eastern Cape">Eastern Cape</option>
                  <option value="Free State">Free State</option>
                  <option value="Gauteng">Gauteng</option>
                  <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                  <option value="Limpopo">Limpopo</option>
                  <option value="Mpumalanga">Mpumalanga</option>
                  <option value="Northern Cape">Northern Cape</option>
                  <option value="North West">North West</option>
                  <option value="Western Cape">Western Cape</option>
                </select>
                <label htmlFor="orgProvinceSelect">Province *</label>
                {validationErrors.org_province && (
                  <div className="invalid-feedback">{validationErrors.org_province}</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Individual/Representative Address */}
      <h3>{providerType === 'entity' ? 'Representative Address' : 'Current Address'}</h3>
      
      {/* Country - Full width - Locked to South Africa */}
      <div className="form-floating mb-3">
        <input
          className="form-control"
          type="text"
          id="countryInput"
          value="South Africa"
          readOnly
          style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
        />
        <label htmlFor="countryInput">Country</label>
      </div>

      {/* Address Line 1 and 2 - Side by side */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.address_line_1 ? 'is-invalid' : ''}`}
              type="text"
              id="addressLine1Input"
              value={step2Data.address_line_1}
              onChange={(e) => handleStep2Change('address_line_1', e.target.value)}
              placeholder="Address Line 1"
            />
            <label htmlFor="addressLine1Input">Address Line 1 *</label>
            {validationErrors.address_line_1 && (
              <div className="invalid-feedback">{validationErrors.address_line_1}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className="form-control"
              type="text"
              id="addressLine2Input"
              value={step2Data.address_line_2}
              onChange={(e) => handleStep2Change('address_line_2', e.target.value)}
              placeholder="Address Line 2"
            />
            <label htmlFor="addressLine2Input">Address Line 2</label>
          </div>
        </div>
      </div>

      {/* Suburb and City - Side by side */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.suburb ? 'is-invalid' : ''}`}
              type="text"
              id="suburbInput"
              value={step2Data.suburb}
              onChange={(e) => handleStep2Change('suburb', e.target.value)}
              placeholder="Suburb"
            />
            <label htmlFor="suburbInput">Suburb *</label>
            {validationErrors.suburb && (
              <div className="invalid-feedback">{validationErrors.suburb}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.city ? 'is-invalid' : ''}`}
              type="text"
              id="cityInput"
              value={step2Data.city}
              onChange={(e) => handleStep2Change('city', e.target.value)}
              placeholder="City"
            />
            <label htmlFor="cityInput">City *</label>
            {validationErrors.city && (
              <div className="invalid-feedback">{validationErrors.city}</div>
            )}
          </div>
        </div>
      </div>

      {/* Postcode and Province - Side by side */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.postcode ? 'is-invalid' : ''}`}
              type="text"
              id="postcodeInput"
              value={step2Data.postcode}
              onChange={(e) => handleStep2Change('postcode', e.target.value)}
              placeholder="Postcode"
            />
            <label htmlFor="postcodeInput">Postcode *</label>
            {validationErrors.postcode && (
              <div className="invalid-feedback">{validationErrors.postcode}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.province ? 'is-invalid' : ''}`}
              id="provinceSelect"
              value={step2Data.province}
              onChange={(e) => handleStep2Change('province', e.target.value)}
            >
              <option value="">Select Province</option>
              <option value="Eastern Cape">Eastern Cape</option>
              <option value="Free State">Free State</option>
              <option value="Gauteng">Gauteng</option>
              <option value="KwaZulu-Natal">KwaZulu-Natal</option>
              <option value="Limpopo">Limpopo</option>
              <option value="Mpumalanga">Mpumalanga</option>
              <option value="Northern Cape">Northern Cape</option>
              <option value="North West">North West</option>
              <option value="Western Cape">Western Cape</option>
            </select>
            <label htmlFor="provinceSelect">Province *</label>
            {validationErrors.province && (
              <div className="invalid-feedback">{validationErrors.province}</div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="row mt-4">
        <div className="col-md-6">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handlePrevious}
          >
            Previous
          </button>
        </div>
        <div className="col-md-6 text-end">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loadingStates.step2}
          >
            {loadingStates.step2 ? 'Submitting...' : 'Continue to Bank Details'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Step 3: Bank Details
const Step3 = ({
  step3Data,
  handleStep3Change,
  validateStep3,
  submitStep3Data,
  loadingStates,
  previousStep,
  nextStep,
  validationErrors,
  setValidationErrors,
  uploadedDocuments,
  uploadingDocuments,
  uploadedFilenames,
  documentErrors,
  handleDocumentUploaded,
  handleDocumentUploadFailed,
  handleUploadStart
}) => {

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateStep3();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setValidationErrors({});
    const success = await submitStep3Data();
    if (success) {
      nextStep();
    }
  };

  const handlePrevious = () => {
    setValidationErrors({});
    previousStep();
  };



  return (
    <div>
      <h3>Bank Details</h3>
      
      {/* Bank Name */}
      <div className="form-floating mb-3">
        <input
          className={`form-control ${validationErrors.bank_name ? 'is-invalid' : ''}`}
          type="text"
          id="bankNameInput"
          value={step3Data.bank_name}
          onChange={(e) => handleStep3Change('bank_name', e.target.value)}
          placeholder="Bank Name"
        />
        <label htmlFor="bankNameInput">Bank Name *</label>
        {validationErrors.bank_name && (
          <div className="invalid-feedback">{validationErrors.bank_name}</div>
        )}
      </div>

      {/* Account Number and Account Type - Side by side */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.account_number ? 'is-invalid' : ''}`}
              type="text"
              id="accountNumberInput"
              value={step3Data.account_number}
              onChange={(e) => handleStep3Change('account_number', e.target.value)}
              placeholder="Account Number"
            />
            <label htmlFor="accountNumberInput">Account Number *</label>
            {validationErrors.account_number && (
              <div className="invalid-feedback">{validationErrors.account_number}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.account_type ? 'is-invalid' : ''}`}
              id="accountTypeSelect"
              value={step3Data.account_type}
              onChange={(e) => handleStep3Change('account_type', e.target.value)}
            >
              <option value="">Select Account Type</option>
              <option value="current">Current Account</option>
              <option value="savings">Savings Account</option>
              <option value="business">Business Account</option>
              <option value="transmission">Transmission Account</option>
            </select>
            <label htmlFor="accountTypeSelect">Account Type *</label>
            {validationErrors.account_type && (
              <div className="invalid-feedback">{validationErrors.account_type}</div>
            )}
          </div>
        </div>
      </div>

      {/* Proof of Account Upload */}
      <div className="mb-4">
        <label className="form-label">Proof of Account *</label>
        <ProviderDragDropUpload
          documentType="proof_of_account"
          title="Proof of Bank Account"
          onDocumentUploaded={handleDocumentUploaded}
          onDocumentUploadFailed={handleDocumentUploadFailed}
          uploadedFilenames={uploadedFilenames}
          isUploading={uploadingDocuments.has('proof_of_account')}
          onUploadStart={handleUploadStart}
          hasError={documentErrors.has('proof_of_account')}
        />
        {validationErrors.proof_of_account && (
          <div className="text-danger mt-2">{validationErrors.proof_of_account}</div>
        )}
        {step3Data.proof_of_account && (
          <div className="mt-2 text-success">
            <i className="bi bi-check-circle"></i> File uploaded: {step3Data.proof_of_account.name}
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="row mt-4">
        <div className="col-md-6">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handlePrevious}
          >
            Previous
          </button>
        </div>
        <div className="col-md-6 text-end">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loadingStates.step3}
          >
            {loadingStates.step3 ? 'Submitting...' : 'Complete Registration'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Provider Form Wizard Component
const ProviderFormWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardCompleted, setWizardCompleted] = useState(false);
  
  // Get provider type from localStorage (set during login)
  const providerType = localStorage.getItem('provider_type');

  const stepLabels = [
    { label: "Identification", icon: "bi-person-badge" },
    { label: "Address", icon: "bi-geo-alt" },
    { label: "Bank Details", icon: "bi-bank" }
  ];

  const [step1Data, setStep1Data] = useState({
    // Common fields for both individual and entity
    citizenship: '',
    id_document_type: '',
    id_number: '',
    phone_number: '',
    
    // Entity-specific fields
    organization_phone: '', // For entities
    registration_number: '' // For entities
  });

  const [step2Data, setStep2Data] = useState({
    // Individual/Representative address
    country: 'South Africa',
    address_line_1: '',
    address_line_2: '',
    suburb: '',
    city: '',
    postcode: '',
    province: '',
    
    // Entity-specific organization address
    org_country: 'South Africa',
    org_address_line_1: '',
    org_address_line_2: '',
    org_suburb: '',
    org_city: '',
    org_postcode: '',
    org_province: ''
  });

  const [step3Data, setStep3Data] = useState({
    bank_name: '',
    account_number: '',
    account_type: '',
    proof_of_account: null
  });
  
  const [loadingStates, setLoadingStates] = useState({ 
    step1: false,
    step2: false,
    step3: false
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Document upload state
  const [selectedDocument, setSelectedDocument] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState(new Set());
  const [uploadedFilenames, setUploadedFilenames] = useState(new Map());
  const [documentErrors, setDocumentErrors] = useState(new Set());
  const [uploadingDocuments, setUploadingDocuments] = useState(new Set());

  // Get required documents based on provider type
  const getRequiredDocuments = () => {
    console.log('getRequiredDocuments called, providerType:', providerType);
    const required = [];
    
    if (providerType === 'individual') {
      required.push('id_passport');
    } else if (providerType === 'entity') {
      required.push('company_registration');
    }
    
    console.log('Required documents:', required);
    return required;
  };

  // Reset all uploading states
  const resetAllUploadingStates = useCallback(() => {
    setUploadingDocuments(new Set());
  }, []);

  // Handle successful document upload
  const handleDocumentUploaded = useCallback((docType, filename) => {
    setUploadingDocuments(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
    setUploadedDocuments(prev => new Set([...(prev || []), docType]));
    
    setUploadedFilenames(prev => {
      const newMap = new Map(prev);
      const existingFiles = newMap.get(docType) || [];
      const updatedFiles = [...existingFiles, filename];
      newMap.set(docType, updatedFiles);
      return newMap;
    });
    
    setDocumentErrors(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
    setSelectedDocument('');
  }, []);

  // Handle failed document upload
  const handleDocumentUploadFailed = useCallback((docType) => {
    setUploadingDocuments(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
    setDocumentErrors(prev => new Set([...(prev || []), docType]));
    setSelectedDocument('');
  }, []);

  // Handle upload start
  const handleUploadStart = useCallback((docType) => {
    setUploadingDocuments(prev => new Set([...(prev || []), docType]));
  }, []);

  // Validate documents
  const validateDocuments = () => {
    try {
      const required = getRequiredDocuments();
      const missing = required.filter(doc => !uploadedDocuments.has(doc));
      setDocumentErrors(new Set(missing));
      return missing.length === 0;
    } catch (error) {
      console.error('Error in validateDocuments:', error);
      return false;
    }
  };

  // Get document card state
  const getDocumentCardState = (docType) => {
    if (uploadedDocuments.has(docType)) {
      return 'uploaded';
    } else if (uploadingDocuments.has(docType)) {
      return 'uploading';
    } else if (documentErrors.has(docType)) {
      return 'error';
    }
    return 'normal';
  };

  const handleDocumentClick = (docType) => {
    if (uploadingDocuments.has(docType)) {
      return;
    }
    
    setDocumentErrors(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
    
    setSelectedDocument(docType);
  };

  const handleClearSelection = useCallback(() => {
    resetAllUploadingStates();
    setSelectedDocument('');
  }, [resetAllUploadingStates]);

  // Step 1 handlers
  const handleStep1Change = (field, value) => {
    setStep1Data(prev => ({ ...prev, [field]: value }));
  };

  // Step 2 handlers
  const handleStep2Change = (field, value) => {
    setStep2Data(prev => ({ ...prev, [field]: value }));
  };

  // Step 3 handlers
  const handleStep3Change = (field, value) => {
    setStep3Data(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    const errors = {};
    
    // Common validations
    if (!step1Data.citizenship) errors.citizenship = 'Citizenship is required';
    if (!step1Data.id_document_type) errors.id_document_type = 'ID document type is required';
    if (!step1Data.id_number) errors.id_number = 'ID number is required';
    if (!step1Data.phone_number) {
      errors.phone_number = 'Phone number is required';
    } else if (step1Data.phone_number.length < 8) {
      errors.phone_number = 'Please enter a valid phone number';
    }
    
    // Entity-specific validations
    if (providerType === 'entity') {
      if (!step1Data.registration_number) errors.registration_number = 'Business registration number is required';
      if (!step1Data.organization_phone) {
        errors.organization_phone = 'Organization phone number is required';
      } else if (step1Data.organization_phone.length < 8) {
        errors.organization_phone = 'Please enter a valid organization phone number';
      }
    }
    
    return errors;
  };

  const validateStep2 = () => {
    const errors = {};
    
    // Individual/Representative address validations
    if (!step2Data.address_line_1) errors.address_line_1 = 'Address line 1 is required';
    if (!step2Data.suburb) errors.suburb = 'Suburb is required';
    if (!step2Data.city) errors.city = 'City is required';
    if (!step2Data.postcode) errors.postcode = 'Postcode is required';
    if (!step2Data.province) errors.province = 'Province is required';
    
    // Entity-specific organization address validations
    if (providerType === 'entity') {
      if (!step2Data.org_address_line_1) errors.org_address_line_1 = 'Organization address line 1 is required';
      if (!step2Data.org_suburb) errors.org_suburb = 'Organization suburb is required';
      if (!step2Data.org_city) errors.org_city = 'Organization city is required';
      if (!step2Data.org_postcode) errors.org_postcode = 'Organization postcode is required';
      if (!step2Data.org_province) errors.org_province = 'Organization province is required';
    }
    
    return errors;
  };

  const submitStep2Data = async () => {
    setLoadingStates(prev => ({ ...prev, step2: true }));
    
    try {
      const userId = localStorage.getItem('user_id');
      
      // Prepare payload for updating address details
      const payload = {
        personal_address: `${step2Data.address_line_1}${step2Data.address_line_2 ? ', ' + step2Data.address_line_2 : ''}, ${step2Data.suburb}, ${step2Data.city}, ${step2Data.province}, ${step2Data.postcode}, South Africa`,
        reg_status: 1 // Mark registration as complete
      };

      // Add entity-specific organization address if applicable
      if (providerType === 'entity') {
        payload.business_address = `${step2Data.org_address_line_1}${step2Data.org_address_line_2 ? ', ' + step2Data.org_address_line_2 : ''}, ${step2Data.org_suburb}, ${step2Data.org_city}, ${step2Data.org_province}, ${step2Data.org_postcode}, South Africa`;
      }

      // Update provider details based on type
      const endpoint = providerType === 'entity' 
        ? `/accounts/providers/entity/${userId}/`
        : `/accounts/providers/individual/${userId}/`;
      
      await axios.patch(endpoint, payload);
      
      return true;
    } catch (error) {
      console.error('Error submitting step 2 data:', error);
      return false;
    } finally {
      setLoadingStates(prev => ({ ...prev, step2: false }));
    }
  };

  const validateStep3 = () => {
    const errors = {};
    
    if (!step3Data.bank_name) errors.bank_name = 'Bank name is required';
    if (!step3Data.account_number) errors.account_number = 'Account number is required';
    if (!step3Data.account_type) errors.account_type = 'Account type is required';
    if (!uploadedDocuments.has('proof_of_account')) errors.proof_of_account = 'Proof of account is required';
    
    return errors;
  };

  const submitStep3Data = async () => {
    setLoadingStates(prev => ({ ...prev, step3: true }));
    
    try {
      const userId = localStorage.getItem('user_id');
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('bank_name', step3Data.bank_name);
      formData.append('account_number', step3Data.account_number);
      formData.append('account_type', step3Data.account_type);
      if (step3Data.proof_of_account) {
        formData.append('proof_of_account', step3Data.proof_of_account);
      }
      formData.append('reg_status', 1); // Mark registration as submitted, pending review

      // Update provider details based on type
      const endpoint = providerType === 'entity' 
        ? `/accounts/providers/entity/${userId}/`
        : `/accounts/providers/individual/${userId}/`;
      
      await axios.patch(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return true;
    } catch (error) {
      console.error('Error submitting step 3 data:', error);
      return false;
    } finally {
      setLoadingStates(prev => ({ ...prev, step3: false }));
    }
  };

  const submitStep1Data = async () => {
    setLoadingStates(prev => ({ ...prev, step1: true }));
    
    try {
      const userId = localStorage.getItem('user_id');
      
      // Prepare payload for updating provider details
      const payload = {
        contact_phone: step1Data.phone_number,
        id_type: step1Data.id_document_type,
        id_no: step1Data.id_number,
        personal_address: step1Data.address,
        citizenship: step1Data.citizenship,
        reg_status: 1 // Mark registration as complete
      };

      // Add entity-specific fields if applicable
      if (providerType === 'entity') {
        payload.business_phone = step1Data.organization_phone;
        payload.business_address = step1Data.organization_address;
        payload.registration_number = step1Data.registration_number;
      }

      // Update provider details based on type
      const endpoint = providerType === 'entity' 
        ? `/accounts/providers/entity/${userId}/`
        : `/accounts/providers/individual/${userId}/`;
      
      await axios.patch(endpoint, payload);
      
      // Update reg_status in localStorage
      localStorage.setItem('reg_status', '1');
      
      return true;
    } catch (error) {
      console.error('Error submitting step 1 data:', error);
      return false;
    } finally {
      setLoadingStates(prev => ({ ...prev, step1: false }));
    }
  };

  const nextStep = () => {
    if (currentStep < stepLabels.length) {
      setCurrentStep(prev => prev + 1);
    } else {
      setWizardCompleted(true);
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (wizardCompleted) {
    return <CompletionSuccess />;
  }

  return (
    <>
      <div className="wizardbox">
        <div className="step-indicator mb-4 d-flex justify-content-between align-items-end" style={{ gap: 0 }}>
          {stepLabels.map((step, index) => {
            const stepNum = index + 1;
            const isActive = currentStep === stepNum;
            const isCompleted = currentStep > stepNum;
            
            return (
              <div
                key={stepNum}
                className={`step text-center ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                style={{
                  flex: 1,
                  position: "relative",
                  zIndex: 1,
                  minWidth: 100,
                  cursor: 'default'
                }}
              >
                <div
                  className={`circle d-flex align-items-center justify-content-center mb-2 ${isActive ? "circle-active" : ""} ${isCompleted ? "circle-completed" : ""}`}
                  style={{
                    width: 60,
                    height: 60,
                    margin: "0 auto",
                    borderRadius: "50%",
                    background: isActive
                      ? "linear-gradient(135deg, #296f93 60%, #5bc0de 100%)"
                      : isCompleted
                      ? "linear-gradient(135deg, #28a745 60%, #20c997 100%)"
                      : "#e9ecef",
                    color: isActive || isCompleted ? "#fff" : "#adb5bd",
                    fontSize: 32,
                    boxShadow: isActive
                      ? "0 0 0 4px #296f9333"
                      : isCompleted
                      ? "0 0 0 2px #28a74533"
                      : "none",
                    border: isActive ? "2px solid #296f93" : isCompleted ? "2px solid #28a745" : "2px solid #dee2e6",
                    transition: "all 0.2s"
                  }}
                >
                  <i className={`bi ${step.icon}`} />
                </div>
                <div
                  className="label small fw-bold"
                  style={{
                    color: isActive
                      ? "#296f93"
                      : isCompleted
                      ? "#28a745"
                      : "#6c757d",
                    fontSize: isActive ? 18 : 15,
                    marginTop: 4,
                    letterSpacing: 0.5,
                    transition: "color 0.2s, font-size 0.2s"
                  }}
                >
                  {step.label}
                </div>
                {/* Connection line between steps */}
                {index < stepLabels.length - 1 && (
                  <div
                    style={{
                      position: "absolute",
                      top: 30,
                      right: -50,
                      width: 100,
                      height: 6,
                      background: isCompleted
                        ? "linear-gradient(90deg, #28a745 60%, #20c997 100%)"
                        : "#dee2e6",
                      zIndex: 0,
                      borderRadius: 3,
                      transition: "background 0.2s"
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {currentStep === 1 && (
          <Step1
            step1Data={step1Data}
            handleStep1Change={handleStep1Change}
            validateStep1={validateStep1}
            submitStep1Data={submitStep1Data}
            loadingStates={loadingStates}
            nextStep={nextStep}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            uploadedDocuments={uploadedDocuments}
            uploadingDocuments={uploadingDocuments}
            uploadedFilenames={uploadedFilenames}
            getDocumentCardState={getDocumentCardState}
            handleDocumentClick={handleDocumentClick}
            resetAllUploadingStates={resetAllUploadingStates}
            documentErrors={documentErrors}
            validateDocuments={validateDocuments}
            selectedDocument={selectedDocument}
            handleDocumentUploaded={handleDocumentUploaded}
            handleDocumentUploadFailed={handleDocumentUploadFailed}
            handleClearSelection={handleClearSelection}
            handleUploadStart={handleUploadStart}
            providerType={providerType}
          />
        )}

        {currentStep === 2 && (
          <Step2
            step2Data={step2Data}
            handleStep2Change={handleStep2Change}
            validateStep2={validateStep2}
            submitStep2Data={submitStep2Data}
            loadingStates={loadingStates}
            previousStep={previousStep}
            nextStep={nextStep}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            providerType={providerType}
          />
        )}

        {currentStep === 3 && (
          <Step3
            step3Data={step3Data}
            handleStep3Change={handleStep3Change}
            validateStep3={validateStep3}
            submitStep3Data={submitStep3Data}
            loadingStates={loadingStates}
            previousStep={previousStep}
            nextStep={nextStep}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            uploadedDocuments={uploadedDocuments}
            uploadingDocuments={uploadingDocuments}
            uploadedFilenames={uploadedFilenames}
            documentErrors={documentErrors}
            handleDocumentUploaded={handleDocumentUploaded}
            handleDocumentUploadFailed={handleDocumentUploadFailed}
            handleUploadStart={handleUploadStart}
          />
        )}
      </div>
      
      <style>
        {`
          .lightbox-backdrop {
            animation: fadeIn 0.2s;
          }
          
          @keyframes fadeIn {
            from { opacity: 0 }
            to { opacity: 1 }
          }
        `}
      </style>
    </>
  );
};

export default ProviderFormWizard;